import { ZfAccordion, ZfAccordionItem, ZfBanner, ZfDialog, ZfDialogContent, ZfDialogFooter, ZfIcon, ZfStack, ZfTextButton, ZfTypography } from '@zafire/react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfiguration } from '../api/configuration';
import type { HttpProblem } from '../api/generated/client';
import { bannerService } from './BannerService';
import { BannerContext } from './bannerContext';

export function BannerProvider({ children }: { children: ReactNode }) {
    const [problem, setProblem] = useState<HttpProblem | null>(null);
    const [showDialog, setShowDialog] = useState(false);
    const [copied, setCopied] = useState(false);
    const { t } = useTranslation();
    const { data: frontendConfiguration } = useConfiguration();

    const handleCopy = async () => {
        if (!problem) return;
        try {
            await navigator.clipboard.writeText(JSON.stringify(problem, null, 2));
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    const handleSendMail = async () => {
        if (!problem) return;

        try {
            const problemText = JSON.stringify(problem, null, 2);
            await navigator.clipboard.writeText(problemText);

            const recipient = frontendConfiguration?.supportMail;
            const subject = encodeURIComponent(`YWU Incident // Date: ${problem.timestampUTC}; User: ${problem.user}`);
            const body = encodeURIComponent(`Service ID: ${problem.serviceId}\n\n${problemText}`);

            window.location.href = `mailto:${recipient}?subject=${subject}&body=${body}`;
        } catch (err) {
            console.error('Failed to open email:', err);
        }
    };

    useEffect(() => {
        setShowDialog(!!problem);
    }, [problem]);

    useEffect(() => bannerService.subscribe(setProblem), []);

    const onDismiss = () => bannerService.clear();

    return (
        <BannerContext.Provider value={{ show: (p: HttpProblem) => bannerService.show(p), clear: onDismiss }}>
            <>
                {children}
                <ZfDialog size={'small'} show={showDialog} onDialogClose={() => setShowDialog(false)}>
                    <ZfDialogContent>
                        <div className="partner-error-content ">
                            <div>
                                <ZfIcon name={'warning'} size={'extra-large'} className={'partner-error-content__icon'} />
                            </div>
                            <div className="partner-error-content__error-title">
                                {t('error')}
                                {problem?.title ? `: ${problem.title}` : ''}
                            </div>
                            <div className="partner-error-content__error-text">
                                <ZfTypography alignment={'center'} style={{ margin: '1rem' }}>
                                    {t('genericErrorMessage')}
                                </ZfTypography>
                            </div>
                            <div className="partner-error-content__error-list">
                                <ZfAccordion type="multiple">
                                    <ZfAccordionItem size="medium" titleText={t('whatYouCanTry')} showUnderline={true} state={'active'}>
                                        <div className="body regular medium">
                                            <ZfTypography>
                                                <strong>{t('europeAmerica')}</strong>
                                                {t('supportMessageEuropeAmerica')}
                                            </ZfTypography>
                                            <ZfTypography>
                                                <strong>{t('asia')}</strong>
                                                {t('supportMessageAsia')}
                                            </ZfTypography>
                                            <ZfBanner
                                                style={{ marginTop: '1rem', marginBottom: '1rem' }}
                                                description={t('technicalDetailsIndication')}
                                                dismissible={false}
                                                type={'info'}
                                                hasLeadingIcon={false}
                                            />
                                        </div>
                                    </ZfAccordionItem>
                                    <ZfAccordionItem size="medium" titleText={t('technicalDetails')} showUnderline={true} state={'active'}>
                                        <ZfTypography style={{ marginBottom: '1rem' }}>{problem?.detail}</ZfTypography>
                                        <ZfTypography>
                                            <pre
                                                style={{
                                                    whiteSpace: 'pre-wrap',
                                                    wordBreak: 'break-word',
                                                    margin: 0,
                                                    backgroundColor: '#f5f5f5',
                                                    padding: '10px',
                                                    borderRadius: '5px',
                                                    fontFamily: 'monospace',
                                                    overflowX: 'auto',
                                                }}
                                            >
                                                {JSON.stringify(problem, null, 2)}
                                            </pre>
                                        </ZfTypography>
                                    </ZfAccordionItem>
                                </ZfAccordion>
                            </div>
                        </div>
                    </ZfDialogContent>
                    <ZfDialogFooter>
                        <ZfStack slot="primary" direction="row" spacing="small">
                            <ZfTextButton size="medium" hierarchy="tertiary" onClick={() => setShowDialog(false)}>
                                {t('cancel')}
                            </ZfTextButton>
                            <ZfTextButton size="medium" onClick={handleSendMail}>
                                {copied ? t('copied') : t('createIncident')}
                            </ZfTextButton>
                            <ZfTextButton size="medium" onClick={handleCopy}>
                                {copied ? t('copied') : t('copyTechnicalInformation')}
                            </ZfTextButton>
                        </ZfStack>
                    </ZfDialogFooter>
                </ZfDialog>
            </>
        </BannerContext.Provider>
    );
}
